<template>
  <PageProductDetail
    v-if="product"
    :product="product"
    :configurator-data="configuratorData"
    :menu-item="menuItem"
    :route-configuration="routeConfiguration"
  />
</template>

<script>
import { get, merge } from 'lodash'
import Component from '~/sites/shop/pages/product/_slug'
import gql from 'graphql-tag'

const nuxtI18nPaths = {
  cs: '/produkt/:slug',
  de: '/produkt/:slug',
  en: '/product/:slug',
  sk: '/produkt/:slug',
}

const loadProductConfigurationTypesData = async app => {
  const { data } = await app.apolloProvider.defaultClient.query({
    query: gql`
      query ProductConfigurationType {
        product_configuration_type {
          entities {
            _id
            product_configuration_type_code
            title
            description
            required
            larger_images
          }
        }
      }
    `,
  })
  return data.product_configuration_type.entities
}

const toPositiveNumber = value => {
  const number = Number(value)

  return Number.isFinite(number) && number > 0 ? number : null
}

const parseProductSlug = slug => {
  const value = slug || ''
  const dashDimensions = value.match(/^(.*)-(\d+)-(\d+)$/)
  const compactDimensions = value.match(/^(.*)-(\d+)x(\d+)$/)
  const match = dashDimensions || compactDimensions

  if (!match) {
    return {
      baseSlug: value,
      width: null,
      height: null,
      dimensionFormat: null,
    }
  }

  return {
    baseSlug: match[1],
    width: toPositiveNumber(match[2]),
    height: toPositiveNumber(match[3]),
    dimensionFormat: dashDimensions ? 'dash' : 'compact',
  }
}

const getProductNameDimensions = slug => {
  const match = (slug || '').match(/^(.*)-(\d+)x(\d+)-cm$/)

  if (!match) {
    return null
  }

  const width = toPositiveNumber(match[2])
  const height = toPositiveNumber(match[3])

  return width && height ? { width, height } : null
}

const getProductSlugConfiguration = (slug, product = null) => {
  const parsedSlug = parseProductSlug(slug)

  if (parsedSlug.dimensionFormat === 'compact' && product?.slug === slug) {
    return {
      baseSlug: slug,
      width: null,
      height: null,
      dimensionFormat: null,
      isCompactDimensionFormat: true,
      isExactCompactProduct: true,
    }
  }

  return {
    ...parsedSlug,
    isCompactDimensionFormat: parsedSlug.dimensionFormat === 'compact',
    isExactCompactProduct: false,
  }
}

const getConfigurationPrices = product => {
  return get(product, 'content.product_configurations_priced', []).reduce((prices, configuration) => {
    return [...prices, ...(configuration.product_configuration_value_prices || [])]
  }, [])
}

const hasConfigurationPrices = product => getConfigurationPrices(product).length > 0

const hasPositivePrice = item => {
  const price = Number(item?.price)

  return Number.isFinite(price) && price > 0
}

const toDimensions = item => {
  const width = toPositiveNumber(item?.max_width)
  const height = toPositiveNumber(item?.max_height)

  return width && height ? { width, height } : null
}

const getDefaultProductDimensions = product => {
  const attributes = product?.attributes || {}
  const limits = attributes?.limits || {}

  const dimensions =
    (toPositiveNumber(limits?.min_dimension?.width) &&
      toPositiveNumber(limits?.min_dimension?.height) && {
        width: toPositiveNumber(limits.min_dimension.width),
        height: toPositiveNumber(limits.min_dimension.height),
      }) ||
    (toPositiveNumber(limits?.min_width) &&
      toPositiveNumber(limits?.min_height) && {
        width: toPositiveNumber(limits.min_width),
        height: toPositiveNumber(limits.min_height),
      }) ||
    (toPositiveNumber(attributes?.width) &&
      toPositiveNumber(attributes?.height) && {
        width: toPositiveNumber(attributes.width),
        height: toPositiveNumber(attributes.height),
      })

  if (dimensions) {
    return dimensions
  }

  const defaultPrice = getConfigurationPrices(product).find(hasPositivePrice)

  return toDimensions(defaultPrice)
}

const findNearestConfigurationDimensions = (product, width, height) => {
  return getConfigurationPrices(product)
    .filter(hasPositivePrice)
    .map(toDimensions)
    .filter(Boolean)
    .filter(dimensions => dimensions.width >= width && dimensions.height >= height)
    .sort((a, b) => a.width - b.width || a.height - b.height)[0]
}

const getRedirectDimensions = (product, requestedDimensions = {}) => {
  const { width, height } = requestedDimensions

  if (width && height) {
    return findNearestConfigurationDimensions(product, width, height) || getDefaultProductDimensions(product)
  }

  return getDefaultProductDimensions(product)
}

const getProductPath = (app, slug) => app.localePath({ name: 'product-slug', params: { slug } })

const getDefaultDimensions = (product, fallback = {}) => {
  const attributes = product?.attributes || {}
  const width =
    toPositiveNumber(fallback.width) ||
    toPositiveNumber(attributes?.width) ||
    toPositiveNumber(attributes?.limits?.min_dimension?.width) ||
    toPositiveNumber(attributes?.limits?.min_width)
  const height =
    toPositiveNumber(fallback.height) ||
    toPositiveNumber(attributes?.height) ||
    toPositiveNumber(attributes?.limits?.min_dimension?.height) ||
    toPositiveNumber(attributes?.limits?.min_height)

  if (width && height) {
    return { width, height }
  }

  return null
}

const PAGE = {
  extends: Component,
  nuxtI18n: {
    paths: nuxtI18nPaths,
  },
  data() {
    return {
      accessoryProducts: [],
      alternativeProducts: [],
      comments: [],
      productReviews: {},
      relatedCategories: [],
      relatedProducts: [],
    }
  },
  head() {
    const parentHeadDef = (Component && (Component.head || (Component.options && Component.options.head))) || null
    const parentHead = typeof parentHeadDef === 'function' ? parentHeadDef.call(this) : parentHeadDef || {}

    const lang = this.$i18n?.locale || 'cs'
    const slugConfiguration = getProductSlugConfiguration(this.$route?.params?.slug, this.product)
    const base = slugConfiguration.baseSlug
    const fallbackDimensions = slugConfiguration
    const dims = getDefaultDimensions(this.product, fallbackDimensions)
    if (!base || !dims) {
      return parentHead
    }
    const masterWithDims = `${base}-${dims.width}-${dims.height}`
    const path = (nuxtI18nPaths[lang] || '/produkt/:slug')
      .replace('/:slug', `/${masterWithDims}`)
      .replace(/\/\/+/g, '/')
    const canonicalDomains = this.$store.state.locale?.canonicalDomains || []

    let origin = (canonicalDomains.find(d => d.language === lang) || {}).domain
    if (!origin) {
      if (process.server && this.$ssrContext?.req) {
        const req = this.$ssrContext.req
        const proto = req.headers['x-forwarded-proto'] || (req.connection.encrypted ? 'https' : 'http')
        const host = req.headers['x-forwarded-host'] || req.headers.host
        origin = `${proto}://${host}`
      } else if (process.client) {
        origin = window.location.origin
      }
    }

    const href = `${origin}${path}`.replace(/([^:])[\/]{2,}/g, '$1/')

    return {
      ...parentHead,
      link: [...(parentHead.link || []), { rel: 'canonical', href }],
    }
  },
  async asyncData({ store, params, query, res, app, error, redirect }) {
    if (process.server) {
      res.set('X-Inspishop-Cache-Control', `public, max-age=${app.$env.STATELESS_TTL}`)
    }

    const productNameDimensions = getProductNameDimensions(params.slug)
    const product = await store.dispatch('product/LOAD_PRODUCT', { slug: params.slug })
    const { baseSlug, width, height, isCompactDimensionFormat, isExactCompactProduct } = getProductSlugConfiguration(
      params.slug,
      product,
    )

    if ((product?.error || !product) && productNameDimensions && !width && !height) {
      const defaultNamedDimensionSlug = [params.slug, productNameDimensions.width, productNameDimensions.height].join(
        '-',
      )
      const defaultNamedDimensionProduct = await store.dispatch('product/LOAD_PRODUCT', {
        slug: defaultNamedDimensionSlug,
      })

      if (defaultNamedDimensionProduct && !defaultNamedDimensionProduct.error) {
        return redirect(301, getProductPath(app, defaultNamedDimensionSlug), query)
      }

      return error({ statusCode: 404 })
    }

    if ((product?.error || !product) && width && height && baseSlug !== params.slug) {
      const baseProductNameDimensions = getProductNameDimensions(baseSlug)

      if (baseProductNameDimensions) {
        const baseProduct = await store.dispatch('product/LOAD_PRODUCT', { slug: baseSlug })

        if (baseProduct && !baseProduct.error) {
          return redirect(301, getProductPath(app, baseSlug), query)
        }
      }

      const parentProduct = await store.dispatch('product/LOAD_PRODUCT', {
        slug: baseSlug,
        configuration: true,
      })

      if (!parentProduct || parentProduct.error) {
        return app.$pageNotFound({ slug: params.slug, type: 'products' })
      }

      const redirectDimensions = getRedirectDimensions(parentProduct, { width, height })

      if (redirectDimensions) {
        const redirectSlug = [baseSlug, redirectDimensions.width, redirectDimensions.height].join('-')
        return redirect(301, getProductPath(app, redirectSlug), query)
      }
    }

    if (!product) {
      return app.$pageNotFound({ slug: params.slug, type: 'products' })
    }
    if (product?.error) {
      return error({ statusCode: product.error.statusCode })
    }
    if (!Array.isArray(product.variants)) {
      product.variants = []
    }

    if (hasConfigurationPrices(product)) {
      const defaultDimensions = getDefaultProductDimensions(product)
      const defaultSlug = defaultDimensions
        ? [baseSlug, defaultDimensions.width, defaultDimensions.height].join('-')
        : null
      const hasInvalidPrice = Number(product?.prices?.unitWithTax) <= 0
      const shouldRedirectToDefault =
        !isCompactDimensionFormat &&
        !isExactCompactProduct &&
        defaultSlug &&
        (!width || !height || hasInvalidPrice) &&
        params.slug !== defaultSlug

      if (shouldRedirectToDefault) {
        return redirect(301, getProductPath(app, defaultSlug), query)
      }
    }

    const productConfigurationTypes = await loadProductConfigurationTypesData(app)
    if (productConfigurationTypes.length) {
      product.productConfigurationTypes = productConfigurationTypes
    }

    store.commit('locale/SET_CANONICALS', {
      canonicalSlugs: product.canonicalSlugs,
      nuxtI18nPaths,
    })

    const configuratorData = await store.dispatch('configurator/LOAD_CONFIGURATOR', {
      id: product.content._id,
    })

    const menuItem = product.categoryId
      ? await store
          .dispatch('menu/FIND_MENU_ITEMS', {
            ids: [product.categoryId, ...product.categoryIds],
            menu: 'mainMenu',
          })
          .then(items => items[0])
      : null

    return {
      product,
      configuratorData,
      menuItem,
      routeConfiguration: getDefaultDimensions(product, { width, height }) || { width: 0, height: 0 },
    }
  },
  methods: {
    async updateProductByAttributes(config) {
      try {
        const requestId =
          config?.extras?.externalId ||
          config?.extras?.productId ||
          this.displayedVariant?.productId ||
          this.displayedVariant?.externalId ||
          this.product?.productId ||
          this.product?.externalId ||
          this.product.id

        const response = await this.$store.dispatch('product/UPDATE_PRODUCT_ATTRIBUTES', {
          id: requestId,
          config,
        })
        if (!response?.success || !response?.isValid || !response.data) {
          return response
        }

        this.$set(this, 'product', merge(this.product, response.data))
        this.updateSlug()
        return response
      } catch (e) {
        console.error(e)
      }
    },
    updateSlug() {
      const routeSlugConfiguration = getProductSlugConfiguration(this.$route?.params?.slug, this.product)
      const productNameDimensions = getProductNameDimensions(routeSlugConfiguration.baseSlug)
      const width =
        toPositiveNumber(get(this.product, 'attributes.width')) ||
        toPositiveNumber(routeSlugConfiguration.width) ||
        productNameDimensions?.width
      const height =
        toPositiveNumber(get(this.product, 'attributes.height')) ||
        toPositiveNumber(routeSlugConfiguration.height) ||
        productNameDimensions?.height
      const slug =
        productNameDimensions && width && height
          ? [routeSlugConfiguration.baseSlug, width, height].join('-')
          : get(this.product, 'slug')
      const goTo = this.localePath({ name: 'product-slug', params: { slug } })

      if (slug && goTo !== window.location.pathname) {
        window.history.pushState({}, null, goTo)
        this.sendProductPageViewEvent()
      }
    },
  },
}

export default PAGE
</script>
